import { Component, CUSTOM_ELEMENTS_SCHEMA, OnDestroy, OnInit } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { AccountInfo, EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { Store } from '@ngxs/store';
import { catchError, filter, from, of, Subject, switchMap, takeUntil } from 'rxjs';

// Actions
import { SetAuthData } from '@app/store/auth/auth.actions';

// State
import { AuthenticationStateModel } from '@app/store/auth/auth.state';

// Services
import { HttpClient } from '@angular/common/http';
import { blobToBase64 } from './utils/binary/binary.utils';
import { LoggingService } from './services/logging/logging.service';

@Component({
  standalone: true,
  selector: 'app-root',
  imports: [RouterOutlet],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'Raven';
  loginDisplay = false;

  private readonly _destroying$ = new Subject<void>();

  constructor(
    private msalBroadcastService: MsalBroadcastService,
    private authService: MsalService,
    private store: Store,
    private http: HttpClient,
    private loggingService: LoggingService, // This is required in order to create the singleton instance as soon as the app starts
  ) {}

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  async ngOnInit(): Promise<void> {
    await this.authService.instance.initialize();
    this.setLoginDisplay();

    /**
     * You can subscribe to MSAL events as shown below. For more info,
     * visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/v2-docs/events.md
     */
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$),
      )
      .subscribe(() => {
        this.setLoginDisplay();
        this.checkAndSetActiveAccount();
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGOUT_SUCCESS),
        takeUntil(this._destroying$),
      )
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .subscribe((result: EventMessage) => {
        this.setLoginDisplay();
        this.checkAndSetActiveAccount();
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS),
        takeUntil(this._destroying$),
      )
      .subscribe((result: EventMessage) => {
        const payload = result.payload as AccountInfo;
        this.authService.instance.setActiveAccount(payload);

        // Get the picture so we can also display it
        this.http
          .get('https://graph.microsoft.com/v1.0/me/photo/$value', {
            responseType: 'blob',
          })
          .pipe(catchError(() => of(void 0)))
          .pipe(switchMap((blob) => (blob ? from(blobToBase64(blob)) : of(void 0))))
          .subscribe((userPicture) => {
            const claims = payload.idTokenClaims;
            const username = payload.username ?? 'Undefined user';
            const authData: AuthenticationStateModel = {
              id: claims?.oid ?? '',
              name: claims?.name ?? '',
              username,
              roles: claims?.roles ?? [],
              ...(userPicture && { picture: userPicture }),
            };

            this.store.dispatch(new SetAuthData(authData));
          });
      });
  }

  setLoginDisplay() {
    this.loginDisplay = this.authService.instance.getAllAccounts().length > 0;
  }

  checkAndSetActiveAccount() {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    const activeAccount = this.authService.instance.getActiveAccount();

    if (!activeAccount && this.authService.instance.getAllAccounts().length > 0) {
      const accounts = this.authService.instance.getAllAccounts();
      // add your code for handling multiple accounts here
      this.authService.instance.setActiveAccount(accounts[0]);
    }
  }
}
